import {
  FlexBoxImageScaleView,
  FlexBoxView,
  getFlexNode,
} from '@play-co/timestep-core/flexbox';
import { View } from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView, {
  LangBitmapOpts,
} from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import {
  sendAddFriendRequest,
  unfriend,
} from 'src/game/friends/friends.sequences';
import { Feature } from 'src/replicant/analytics';
import { UserProfileDetails } from 'src/replicant/friends/friends.asyncGetters';
import { FriendVisitDetails } from 'src/replicant/visit/visit.asyncGetters';
import {
  canAddFriends,
  isBuruFriend,
  isFriendshipRequestedSent,
} from 'src/replicant/friends/friends.getters';
import { user } from 'src/StateObserver';
import { getTimestampText } from 'src/game/visit/visit.util';

const assets = {
  background: 'assets/ui/shared/frames/scroller_panel_blue.png',
  button: 'assets/ui/friends/button_secondary_small.png',
  buttonPressed: 'assets/ui/friends/button_secondary_small_pressed.png',
  buttonDisabled: 'assets/ui/friends/requested_label.png',
  buttonUnfriend: 'assets/ui/friends/button_unfriend.png',
  buttonUnfriendPressed: 'assets/ui/friends/button_unfriend_pressed.png',
};

export class FriendProfileBasicInfoPanel extends FlexBoxImageScaleView {
  private avatar: Avatar;
  private nameLabel: LangBitmapFontTextView;
  private visitLabel: LangBitmapFontTextView;
  private visitedLabel: LangBitmapFontTextView;
  private lastLoginLabel: LangBitmapFontTextView;
  private addFriendButton: ButtonScaleViewWithText;
  private unfriendButton: ButtonScaleViewWithText;
  private data: UserProfileDetails | FriendVisitDetails | undefined;
  private unfriendHandler?: () => Promise<void>;
  private addFriendHandler?: () => void;

  public static get assets() {
    return Object.values(assets);
  }

  constructor(superview: View) {
    super({
      superview,
      image: assets.background,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 19, right: 22 },
        vertical: { top: 19, bottom: 28 },
      },
      flex: {
        width: 526,
        height: 180,
      },
    });

    const wrapper = new FlexBoxView({
      superview: this,
      flex: {
        width: '100%',
        height: '100%',
        paddingHorizontal: 15,
        paddingBottom: 4,
        alignItems: 'center',
      },
    });

    // Create avatar
    this.avatar = new Avatar({
      superview: wrapper,
      width: 128,
      height: 128,
      centerOnOrigin: false,
    });

    // Create content column container
    const contentColumn = new FlexBoxView({
      superview: wrapper,
      flex: {
        flexDirection: 'column',
        flexGrow: 1,
        marginLeft: 12,
        marginRight: 6,
      },
    });

    // Create top row container
    const topRow = new FlexBoxView({
      superview: contentColumn,
      flex: {
        height: 65,
        alignItems: 'center',
        flexGrow: 1,
      },
    });

    // Create bottom row container
    const bottomRow = new FlexBoxView({
      superview: contentColumn,
      flex: {
        flexDirection: 'row',
        flexGrow: 1,
      },
    });

    const baseLabelOpts: LangBitmapOpts = {
      font: 'Body',
      color: '#9A7171',
      verticalAlign: 'center',
    };

    // Create name label
    this.nameLabel = new LangBitmapFontTextView({
      superview: topRow,
      ...baseLabelOpts,
      size: 29,
      height: 48,
    });
    getFlexNode(this.nameLabel).flexGrow = 1;

    // Create add/remove friend buttons container
    const buttonContainer = new FlexBoxView({
      superview: topRow,
      flex: {
        width: 170,
        height: 55,
        marginLeft: 10,
      },
    });

    // Create add friend button
    this.addFriendButton = new ButtonScaleViewWithText({
      superview: buttonContainer,
      image: assets.button,
      imagePressed: assets.buttonPressed,
      imageDisabled: assets.buttonDisabled,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 19, right: 22 },
        vertical: { top: 19, bottom: 26 },
      },
      width: 170,
      height: 55,
      font: 'Body',
      fontSize: 24,
      pressedOffsetY: 2,
      labelOffsetX: -2,
      disabledTextColor: '#7191AB',
      onClick: this.onAddFriend.bind(this),
      tracking: {
        feature: Feature.NEWUI.NAV_FRIENDS.FRIEND_PROFILE._,
        $subFeature: Feature.NEWUI.NAV_FRIENDS.FRIEND_PROFILE.ADD_FRIEND,
      },
    });
    let flex = getFlexNode(this.addFriendButton);
    flex.flexGrow = 1;

    // Create unfriend button
    this.unfriendButton = new ButtonScaleViewWithText({
      superview: buttonContainer,
      image: assets.buttonUnfriend,
      imagePressed: assets.buttonUnfriendPressed,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 16, right: 21 },
        vertical: { top: 17, bottom: 24 },
      },
      width: 170,
      height: 55,
      font: 'Body',
      fontSize: 24,
      pressedOffsetY: 2,
      labelOffsetX: -2,
      localeText: () => i18n('friends.unfriend'),
      onClick: this.onUnfriend.bind(this),
      tracking: {
        feature: Feature.NEWUI.NAV_FRIENDS.FRIEND_PROFILE._,
        $subFeature: Feature.NEWUI.NAV_FRIENDS.FRIEND_PROFILE.UNFRIEND,
      },
    });
    flex = getFlexNode(this.unfriendButton);
    flex.flexGrow = 1;

    // Create visit info column container
    const visitColumn = new FlexBoxView({
      superview: bottomRow,
      flex: {
        flexDirection: 'column',
        flexGrow: 1,
      },
    });

    // Create visit label
    this.visitLabel = new LangBitmapFontTextView({
      superview: visitColumn,
      ...baseLabelOpts,
      size: 20,
      height: 26,
    });

    // Create visited label
    this.visitedLabel = new LangBitmapFontTextView({
      superview: visitColumn,
      ...baseLabelOpts,
      size: 20,
      height: 26,
    });

    // Create last login label
    this.lastLoginLabel = new LangBitmapFontTextView({
      superview: bottomRow,
      ...baseLabelOpts,
      size: 18,
      align: 'right',
      width: 160,
      height: 72,
      wordWrap: true,
    });
    flex = getFlexNode(this.lastLoginLabel);
    flex.alignSelf = 'flex-end';
    flex.marginRight = 5;
  }

  public setUnfriendHandler(handler: () => Promise<void>) {
    this.unfriendHandler = handler;
  }

  public setAddFriendHandler(handler: () => void) {
    this.addFriendHandler = handler;
  }

  public setData(data: UserProfileDetails | FriendVisitDetails) {
    this.data = data;
    this.avatar.updateImage(data.photo!);
    this.avatar.setFrame(data.frame);
    this.nameLabel.localeText = () => data.name!;
    this.visitLabel.localeText = () =>
      i18n('friendsVisit.confirm.visits', data);
    this.visitedLabel.localeText = () =>
      i18n('friendsVisit.confirm.visited', data);
    this.lastLoginLabel.localeText = () =>
      `${i18n('friendsVisit.popup.lastLogin')}\n${getTimestampText(
        data.lastLogin,
      )}`;
    this.updateAddFriendButtonState();
  }

  private async onAddFriend() {
    if (!this.data) return;
    sounds.sfx.play('ok');
    const success = await sendAddFriendRequest(
      {
        id: this.data.id,
        name: this.data.name ?? '',
        profilePicture: this.data.photo ?? '',
        frame: this.data.frame,
        species: this.data.currentEvolution.species,
      },
      { sourceFeature: 'profile' },
    );
    if (success) {
      // Notify the parent popup so it can close and signal the caller (e.g., TabRequests)
      this.addFriendHandler?.();
    }
    this.updateAddFriendButtonState();
  }

  private async onUnfriend() {
    if (!this.data) return;
    sounds.sfx.play('ok');

    // Custom handler for unfriend action, used by FriendProfilePopup to avoid clearing
    if (this.unfriendHandler) {
      await this.unfriendHandler();
      return;
    }

    // The normal unfriend flow, kept for compatibility with other usages of this panel
    await unfriend(this.data.id);
    this.updateAddFriendButtonState();
  }

  private updateAddFriendButtonState() {
    if (
      // Skip if no data
      !this.data ||
      // Or target player's friend list is full
      !this.data.canAcceptFriendRequest ||
      // Or already friends
      isBuruFriend(user(), this.data.id) ||
      // Or if request count exceeded
      !canAddFriends(user())
    ) {
      this.addFriendButton.updateOpts({ visible: false });

      // Show unfriend button if already friends
      this.unfriendButton.updateOpts({
        visible: !!this.data && isBuruFriend(user(), this.data.id),
      });

      return;
    }

    const isRequested = isFriendshipRequestedSent(user(), this.data.id);

    this.unfriendButton.updateOpts({ visible: false });
    this.addFriendButton.updateOpts({ visible: true });
    this.addFriendButton.setDisabled(isRequested);
    this.addFriendButton.label.updateOpts({
      localeText: () =>
        isRequested ? i18n('friends.requested') : i18n('friends.addFriend'),
    });
  }
}
